import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Play, Shield, Zap, Layers, Lock, Brain, Rocket, ChevronDown, ChevronUp, Power, ArrowUpRight } from 'lucide-react';

const CAT_ICONS = { deployment: Rocket, operational: Play, intelligence: Brain, claims: Shield, processing: Zap, structural: Layers, control: Lock };
const CAT_COLORS = { deployment: '#1B4A9A', operational: '#1D9E75', intelligence: '#D96000', claims: '#E24B4A', processing: '#FF9933', structural: '#1B4A9A', control: '#5C6B7A' };

export const STATUS = {
  wired: { label: 'WIRED', bg: 'rgba(29,158,117,0.12)', fg: '#1D9E75' },
  partial: { label: 'PARTIAL', bg: 'rgba(232,160,32,0.14)', fg: '#B07407' },
  described: { label: 'DESCRIBED', bg: 'rgba(136,150,168,0.16)', fg: '#5C6B7A' },
};

export default function OperatingModesPage() {
  const api = useApi();
  const { user } = useAuth();
  const canManage = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [data, setData] = useState(null);
  const [activeModes, setActiveModes] = useState([]);
  const [toggling, setToggling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');

  const loadActive = useCallback(() => {
    api.get('/operating-modes/active').then(r => setActiveModes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [api]);

  useEffect(() => {
    api.get('/operating-modes').then(r => setData(r.data)).catch(err => console.error('API error:', err?.message)).finally(() => setLoading(false));
    loadActive();
  }, [api, loadActive]);

  const isActive = (modeId) => activeModes.some(m => m.mode_id === modeId && m.source !== 'default');

  const toggleMode = async (mode) => {
    setToggling(mode.id);
    try {
      if (isActive(mode.id)) {
        await api.post(`/operating-modes/deactivate?mode_id=${mode.id}`);
        toast.success(`${mode.name} deselected`);
      } else {
        await api.post(`/operating-modes/activate?mode_id=${mode.id}`);
        toast.success(`${mode.name} selected`, {
          description: mode.enforcement?.status === 'wired'
            ? 'Live at the choke point named on the card'
            : 'Recorded and audited — this mode is not wired yet, so runtime behaviour is unchanged',
        });
      }
      loadActive();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mode change failed'); }
    setToggling(null);
  };

  if (loading) return <div data-testid="modes-loading"><Skeleton className="h-96 rounded-sm" /></div>;

  const categories = data?.categories || {};
  const enf = data?.enforcement || {};
  const counts = enf.counts || {};
  const defaultRow = activeModes.find(m => m.source === 'default');

  return (
    <div className="space-y-5" data-testid="operating-modes-page">
      <p className="text-sm max-w-3xl" style={{ color: '#5C6B7A' }}>
        {data?.total_modes} modes across {Object.keys(categories).length} categories — the
        implementation story an MGA buys. Every mode names the choke point that carries it and says
        whether that choke point reads this switch today.
      </p>

      {/* Honest state strip */}
      <Card className="p-4 rounded-sm border bg-white space-y-3" style={{ borderColor: 'rgba(136,150,168,0.25)' }} data-testid="active-modes-strip">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#1D9E75' }}>Selected now</span>
          {defaultRow ? (
            <span className="text-xs" style={{ color: '#5C6B7A' }} data-testid="active-mode-default">
              Nothing selected — the platform runs <strong style={{ color: '#0B1E3D' }}>Exclusive Mode</strong> by
              default, and nothing switches it off
            </span>
          ) : activeModes.map(m => (
            <span key={m.mode_id} className="text-[10px] px-2 py-0.5 rounded-sm font-semibold"
              style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }} data-testid={`active-mode-${m.mode_id}`}>
              {m.name || m.mode_id}
            </span>
          ))}
          {!canManage && <span className="text-[10px] ml-auto" style={{ color: '#64748B' }}>Only MGA admins can change modes</span>}
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          {['all', 'wired', 'partial', 'described'].map(k => {
            const st = STATUS[k];
            const n = k === 'all' ? data?.total_modes : (counts[k] || 0);
            const on = filter === k;
            return (
              <button key={k} onClick={() => setFilter(k)} data-testid={`mode-filter-${k}`}
                className="text-[10px] px-2.5 py-1 rounded-sm font-bold uppercase tracking-wider transition-colors border"
                style={on
                  ? { background: st ? st.bg : 'rgba(27,74,154,0.12)', color: st ? st.fg : '#1B4A9A', borderColor: st ? st.fg : '#1B4A9A' }
                  : { background: 'transparent', color: '#5C6B7A', borderColor: 'rgba(136,150,168,0.3)' }}>
                {k === 'all' ? 'All' : st.label} · {n}
              </button>
            );
          })}
        </div>
        <div className="grid md:grid-cols-3 gap-2">
          {Object.entries(enf.status_meaning || {}).map(([k, v]) => (
            <div key={k} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
              <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color: STATUS[k].fg }}>{STATUS[k].label}</p>
              <p className="text-[11px] mt-0.5 leading-snug" style={{ color: '#5C6B7A' }}>{v}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* The wiring plan */}
      {(enf.waves || []).length > 0 && (
        <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(27,74,154,0.25)' }} data-testid="mode-wave-plan">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#1B4A9A' }}>The wiring plan</p>
          <div className="space-y-2">
            {enf.waves.map(w => (
              <div key={w.wave} className="flex gap-3 items-start" data-testid={`wave-${w.wave}`}>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm shrink-0"
                  style={{ background: 'rgba(27,74,154,0.1)', color: '#1B4A9A' }}>W{w.wave}</span>
                <div>
                  <p className="text-xs font-semibold" style={{ color: '#0B1E3D' }}>{w.name}</p>
                  <p className="text-[11px] mt-0.5 leading-snug" style={{ color: '#5C6B7A' }}>{w.basis}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {w.modes.map(m => (
                      <span key={m} className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono"
                        style={{ background: '#F5F7FA', color: '#5C6B7A' }}>{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {Object.entries(categories).map(([catId, cat]) => {
        const Icon = CAT_ICONS[catId] || Shield;
        const color = CAT_COLORS[catId] || '#5C6B7A';
        const modes = (cat.modes || []).filter(m => filter === 'all' || m.enforcement?.status === filter);
        if (!modes.length) return null;
        return (
          <div key={catId} className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-4 h-4" style={{ color }} />
              <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>{cat.name}</h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: color + '15', color }}>{modes.length} of {cat.count}</span>
              <span className="text-[11px]" style={{ color: '#8896A8' }}>{cat.description}</span>
            </div>
            {modes.map(mode => (
              <ModeCard key={mode.id} mode={mode} color={color}
                isOpen={expanded === mode.id} onToggleOpen={() => setExpanded(expanded === mode.id ? null : mode.id)}
                isActive={isActive(mode.id)} canManage={canManage} toggling={toggling === mode.id}
                onToggle={() => toggleMode(mode)} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ModeCard({ mode, color, isOpen, onToggleOpen, isActive, canManage, toggling, onToggle }) {
  const e = mode.enforcement || {};
  const st = STATUS[e.status] || STATUS.described;
  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden" data-testid={`mode-card-${mode.id}`}>
      <button onClick={onToggleOpen} data-testid={`mode-expand-${mode.id}`}
        className="w-full p-4 flex items-start justify-between text-left hover:bg-[#F5F7FA] transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold" style={{ color: '#0B1E3D' }}>{mode.name}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(136,150,168,0.08)', color: '#5C6B7A' }}>{mode.id}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-wider" style={{ background: st.bg, color: st.fg }} data-testid={`mode-status-${mode.id}`}>{st.label}</span>
            {e.wave && <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(27,74,154,0.08)', color: '#1B4A9A' }}>wave {e.wave}</span>}
            {isActive && <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{ background: 'rgba(29,158,117,0.12)', color: '#1D9E75' }} data-testid={`mode-active-badge-${mode.id}`}>SELECTED</span>}
          </div>
          <p className="text-xs mt-1 leading-relaxed" style={{ color: '#5C6B7A' }}>{mode.description}</p>
          {e.control_surface && (
            <p className="text-[10px] mt-1.5 font-mono truncate" style={{ color: '#8896A8' }} data-testid={`mode-surface-${mode.id}`}>
              ↳ {e.control_surface}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          {canManage && (
            <span role="button" tabIndex={0} data-testid={`mode-toggle-${mode.id}`}
              onClick={(ev) => { ev.stopPropagation(); if (!toggling) onToggle(); }}
              onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.stopPropagation(); onToggle(); } }}
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold border transition-colors"
              style={isActive ? { color: '#E24B4A', borderColor: 'rgba(226,75,74,0.35)' } : { color: '#1D9E75', borderColor: 'rgba(29,158,117,0.35)' }}>
              <Power className="w-3 h-3" />{toggling ? '…' : isActive ? 'Deselect' : 'Select'}
            </span>
          )}
          {isOpen ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
        </div>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-[rgba(136,150,168,0.1)] space-y-3">
          <div className="p-3 rounded-sm mt-3" style={{ background: st.bg, border: `1px solid ${st.fg}33` }} data-testid={`mode-today-${mode.id}`}>
            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: st.fg }}>What happens today</p>
            <p className="text-xs leading-relaxed" style={{ color: '#0B1E3D' }}>{e.today}</p>
            <div className="flex flex-wrap gap-3 mt-2">
              {e.where && <p className="text-[10px] font-mono" style={{ color: '#5C6B7A' }}>{e.where}</p>}
              {e.activate_at && (
                <p className="text-[10px] flex items-center gap-1" style={{ color: '#1B4A9A' }}>
                  <ArrowUpRight className="w-3 h-3" />{e.activate_at}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[{ l: 'Scoring', v: mode.scoring }, { l: 'Binding', v: mode.binding }, { l: 'Claims', v: mode.claims },
              { l: 'Visibility', v: mode.visibility }, { l: 'Automation', v: mode.automation_level },
              { l: 'Human override', v: mode.human_override }, { l: 'Duration', v: mode.duration }].map(f => (
              <div key={f.l} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#5C6B7A' }}>{f.l}</p>
                <p className="text-xs mt-0.5" style={{ color: '#0B1E3D' }}>{f.v}</p>
              </div>
            ))}
          </div>
          <div className="p-3 rounded-sm" style={{ background: 'rgba(217,96,0,0.04)', border: '1px solid rgba(217,96,0,0.1)' }}>
            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#D96000' }}>Use case</p>
            <p className="text-xs" style={{ color: '#0B1E3D' }}>{mode.use_case}</p>
          </div>
          <div className="p-3 rounded-sm" style={{ background: 'rgba(27,74,154,0.04)', border: '1px solid rgba(27,74,154,0.1)' }}>
            <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#1B4A9A' }}>How it sells</p>
            <p className="text-xs leading-relaxed" style={{ color: '#0B1E3D' }}>{mode.practical_scenario}</p>
          </div>
          {mode.kpis && (
            <div>
              <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#5C6B7A' }}>What it is measured on</p>
              <div className="flex flex-wrap gap-1">
                {mode.kpis.map(kpi => <span key={kpi} className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: '#F5F7FA', color: '#0B1E3D' }}>{kpi}</span>)}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
